/**
 * @module footnote/ui/footnoteactionsview
 */

import { icons } from 'ckeditor5/src/core';
import { ButtonView, FocusCycler, LabelView, View, ViewCollection } from 'ckeditor5/src/ui';
import { FocusTracker, KeystrokeHandler } from 'ckeditor5/src/utils';
import '../../theme/footnoteactions.pcss';


/**
 * The footnote actions view class. This view displays footnote preview, allows editing the footnote.
 *
 * @extends {View}
 */
export default class FootnoteActionsView extends View {
	/**
	 * @inheritDoc
	 */
	constructor( locale ) {
		super( locale );

		const t = locale.t;

		/**
		 * @readonly
		 */
		this.focusTracker = new FocusTracker();

		/**
		 * @readonly
		 */
		this.keystrokes = new KeystrokeHandler();

		this.placeholderView = this._createPlaceholderView();
		this.editButtonView = this._createButton( t( 'Editar nota' ), icons.pencil, 'edit' );

		/**
		 * @readonly
		 * @protected
		 */
		this._focusables = new ViewCollection();

		/**
		 * @readonly
		 * @protected
		 */
		this._focusCycler = new FocusCycler( {
			focusables: this._focusables,
			focusTracker: this.focusTracker,
			keystrokeHandler: this.keystrokes,
			actions: {
				focusPrevious: 'shift + tab',
				focusNext: 'tab'
			}
		} );

		this.setTemplate( {
			tag: 'div',

			attributes: {
				class: [
					'ck',
					'ck-footnote-actions', ,
					'ck-responsive-form'
				],
				tabindex: '-1'
			},

			children: [
				this.placeholderView,
				this.editButtonView
			]
		} );
	}

	/**
	 * @inheritDoc
	 */
	render() {
		super.render();

		const childViews = [
			this.placeholderView,
			this.editButtonView
		];

		childViews.forEach( v => {
			this._focusables.add( v );
			this.focusTracker.add( v.element );
		} );

		this.keystrokes.listenTo( this.element );
	}

	focus() {
		this._focusCycler.focusFirst();
	}

	/**
	 * Creates a button view.
	 *
	 * @private
	 * @param {String} label The button label.
	 * @param {String} icon The button's icon.
	 * @param {String} [eventName] An event name that the `ButtonView#execute` event will be delegated to.
	 * @returns {ButtonView} The button view instance.
	 */
	_createButton( label, icon, eventName ) {
		const button = new ButtonView( this.locale );

		button.set( {
			label,
			icon,
			tooltip: true
		} );

		button.delegate( 'execute' ).to( this, eventName );

		return button;
	}

	/**
	 * Creates a placeholder label.
	 *
	 * @private
	 * @returns {LabelView} The button view instance.
	 */
	_createPlaceholderView() {
		const label = new LabelView( this.locale );
		const t = this.t;

		label.extendTemplate( {
			attributes: {
				class: [
					'ck',
					'ck-footnote-actions__placeholder'
				]
			}
		} );

		label.bind( 'text' ).to( this, 'value', ( value ) => {
			return value || t( 'Preencha a nota.' );
		} );

		label.template.tag = 'span';
		label.template.eventListeners = {};

		return label;
	}
}
